import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useJsApiLoader } from '@react-google-maps/api';

import { GOOGLE_API_KEY } from '../../constants/env';
import { GOOGLE_MAPS_LIBRARIES } from '../../components/google-maps/GoogleMapsLibraries';
import { fetchPredictions, fetchLocationByCoords, IPlacesPredictionsResponse } from '../../api/fetchLocation';

interface IUseGeocodingProps {
  input: string | undefined;
  options?: google.maps.places.AutocompleteRequest | google.maps.places.AutocompletionRequest;
  queryOptions?: { refetchOnWindowFocus?: boolean };
  location?: google.maps.LatLng | null | google.maps.LatLngLiteral;
}

const libraries = GOOGLE_MAPS_LIBRARIES;

const usePlacesAutocomplete = ({ input, options, queryOptions, location }: IUseGeocodingProps) => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    i18n: { language },
  } = useTranslation(['components']);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_API_KEY || '',
    // language,
    language: 'es',
    libraries: libraries,
  });

  const { data, refetch } = useQuery<IPlacesPredictionsResponse | undefined>({
    queryKey: ['placesAutocomplete', input],
    queryFn: () => fetchPredictions(input ?? '', options),
    enabled: !!input && input.trim().length > 0,
    ...queryOptions,
  });

  const hasValidLocation =
    !!location &&
    ((location as google.maps.LatLngLiteral).lat !== 0 || (location as google.maps.LatLngLiteral).lng !== 0);

  const { data: locationData, refetch: refetchLocationByCoords } = useQuery<google.maps.GeocoderResponse | undefined>({
    queryKey: ['getLocationDetail', location],
    queryFn: () => fetchLocationByCoords(location ?? null),
    refetchOnWindowFocus: false,
    enabled: false,
  });

  useEffect(() => {
    if (isLoaded && hasValidLocation) void refetchLocationByCoords();
  }, [isLoaded, hasValidLocation, refetchLocationByCoords]);

  const predictions = data?.predictions || [];

  return { predictions, locationData, refetchLocationByCoords, refetch };
};

export default usePlacesAutocomplete;
